<script setup>
import { computed } from "vue";

const props = defineProps({
    selectedItems: {
        type: Set,
        required: true,
    },
    isDownloading: {
        type: Boolean,
        default: false,
    },
    isDeleting: {
        type: Boolean,
        default: false,
    },
    disabledPin: {
        type: Object,
        default: () => ({ pin: false, unpin: false }),
    },
    isPinning: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    "clear-selection",
    "download",
    "pin",
    "unpin",
    "delete",
    "add-to-list",
    "create-list",
]);

const itemCount = computed(() => props.selectedItems.size);

const handleClearSelection = () => {
    emit("clear-selection");
};

const handleDownload = () => {
    emit("download");
};

const handlePin = () => {
    emit("pin");
};

const handleUnpin = () => {
    emit("unpin");
};

const handleDelete = () => {
    emit("delete");
};

const handleAddToList = () => {
    emit("add-to-list");
};

const handleCreateList = () => {
    emit("create-list");
};
</script>

<template>
    <div
        v-if="itemCount > 0"
        class="bulk-actions-footer modal-footer-bar text-center"
    >
        <div class="footer-content">
            <div class="selection-chip">
                <span class="selection-count">
                    {{ itemCount }} Document<span v-if="itemCount > 1">s</span>
                    selected
                </span>
                <button
                    class="clear-btn"
                    @click="handleClearSelection"
                >
                    <img
                        src="@/assets/img/close-circle.png"
                        style="width: 20px; cursor: pointer"
                        alt=""
                    />
                </button>
            </div>
            <div class="action-buttons">
                <button
                    class="action-btn"
                    @click="handleDownload"
                    :disabled="isDownloading"
                >
                    <img
                        v-if="!isDownloading"
                        src="@/assets/img/import.png"
                        style="width: 20px; cursor: pointer"
                        alt=""
                    />
                    <div
                        v-else
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                        style="width: 20px; height: 20px"
                    >
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <span>
                        {{ isDownloading ? "Downloading..." : "Download" }}
                    </span>
                </button>
                <button
                    class="action-btn"
                    @click="handlePin"
                    :disabled="disabledPin.pin || isPinning"
                >
                    <div
                        v-if="isPinning"
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                        style="width: 20px; height: 20px"
                    >
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <img
                        v-else
                        src="@/assets/img/pin1.png"
                        style="width: 20px; cursor: pointer"
                        alt=""
                    />
                    <span>{{ isPinning ? 'Pinning...' : 'Pin' }}</span>
                </button>
                <button
                    class="action-btn"
                    @click="handleUnpin"
                    :disabled="disabledPin.unpin || isPinning"
                >
                    <div
                        v-if="isPinning"
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                        style="width: 20px; height: 20px"
                    >
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <img
                        v-else
                        src="@/assets/img/pin1.png"
                        style="
                            width: 20px;
                            cursor: pointer;
                            filter: sepia(100%) hue-rotate(-15deg)
                                saturate(800%) brightness(120%);
                        "
                        alt=""
                    />
                    <span style="color: darkred">{{ isPinning ? 'Unpinning...' : 'Unpin' }}</span>
                </button>
                <button
                    class="action-btn"
                    @click="handleDelete"
                    :disabled="isDeleting"
                >
                    <img
                        v-if="!isDeleting"
                        src="@/assets/img/delete.png"
                        style="width: 20px; cursor: pointer"
                        alt=""
                    />
                    <div
                        v-else
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                        style="width: 20px; height: 20px"
                    >
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <span>
                        {{ isDeleting ? "Deleting..." : "Delete" }}
                    </span>
                </button>
                <button
                    class="action-btn"
                    @click="handleAddToList"
                >
                    <img
                        src="@/assets/img/add.png"
                        style="width: 20px; cursor: pointer"
                        alt=""
                    />
                    <span>Add to List</span>
                </button>
                <button
                    class="action-btn"
                    @click="handleCreateList"
                >
                    <img
                        src="@/assets/img/menu.png"
                        style="width: 20px; cursor: pointer"
                        alt=""
                    />
                    <span>Create a new list</span>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.bulk-actions-footer {
    background-color: #fff;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    padding: 10px;
    z-index: 100;
    position: fixed !important;
    bottom: 30px !important;
    margin: 0 auto;
    left: 50%;
    transform: translateX(-50%);
    width: fit-content;
    max-width: 1200px;
}

.modal-footer-bar {
    bottom: 0 !important;
}

.footer-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
}

.selection-chip {
    display: flex;
    align-items: center;
    background-color: #f0f0f0;
    padding: 6px 12px;
    border-radius: 20px;
}

.selection-count {
    font-weight: 500;
    margin-right: 8px;
}

.clear-btn {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}

.action-buttons {
    display: flex;
    gap: 10px;
}

.action-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.action-btn:hover {
    background-color: #f0f0f0;
}

.action-btn:disabled {
    cursor: not-allowed;
}

.action-btn:disabled img,
.action-btn:disabled span {
    cursor: not-allowed !important;
}
</style>
